import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search, Users2, UserCheck, UserX, TrendingUp, ChevronLeft, ChevronRight,
  ExternalLink, Power, Wallet, ShoppingBag, Eye, EyeOff, Copy, Pencil, X, Save, Loader2, BookOpen, Megaphone,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import AdminHeader from "@/components/AdminHeader";
import { toast } from "sonner";

function StatCard({ label, value, icon: Icon, tint, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-white/10 p-5 bg-gradient-to-br from-zinc-900 to-black overflow-hidden relative"
    >
      <div className={`absolute -right-6 -top-6 h-24 w-24 rounded-full blur-2xl ${tint}`} />
      <div className="relative">
        <div className="flex items-center gap-2 text-white/70 text-xs uppercase tracking-widest font-semibold">
          <Icon size={14} className="text-emerald-400" /> {label}
        </div>
        <div
          className="mt-2 text-3xl font-extrabold text-white"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, limit: 25 });
  const [loading, setLoading] = useState(true);
  const [revealMap, setRevealMap] = useState({}); // {user_code: bool}
  const [editing, setEditing] = useState(null); // user object being edited or null

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => { setPage(1); }, [debouncedQ]);

  useEffect(() => {
    (async () => {
      try {
        const s = await api.get("/admin/stats");
        setStats(s.data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/users", {
          params: { q: debouncedQ || undefined, page, limit: 25 },
        });
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [debouncedQ, page]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(data.total / (data.limit || 25))),
    [data.total, data.limit]
  );

  const toggleActive = async (code) => {
    try {
      const { data: r } = await api.post(`/admin/users/${encodeURIComponent(code)}/toggle-active`);
      toast.success(`${code} is now ${r.activated ? "Active" : "Inactive"}`);
      setData((prev) => ({
        ...prev,
        items: prev.items.map((u) => (u.user_code === code ? { ...u, activated: r.activated } : u)),
      }));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyToClipboard = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const reloadUsers = async () => {
    try {
      const { data } = await api.get("/admin/users", {
        params: { q: debouncedQ || undefined, page, limit: 25 },
      });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const applyUserUpdate = (updated) => {
    setData((prev) => ({
      ...prev,
      items: prev.items.map((u) =>
        u.user_code === updated.user_code ? { ...u, ...updated } : u
      ),
    }));
  };

  return (
    <div className="min-h-screen text-white" style={{ background: "#050506" }}>
      <AdminHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <div className="flex flex-wrap gap-3">
          <Link
            to="/admin/deposits"
            data-testid="admin-nav-deposits"
            className="inline-flex items-center gap-2 rounded-lg border border-emerald-400/40 text-emerald-400 hover:bg-emerald-400 hover:text-black px-4 py-2 text-sm font-semibold transition-colors"
          >
            <Wallet size={16} /> Fund Deposits
          </Link>
          <Link
            to="/admin/products"
            data-testid="admin-nav-products"
            className="inline-flex items-center gap-2 rounded-lg border border-emerald-400/40 text-emerald-400 hover:bg-emerald-400 hover:text-black px-4 py-2 text-sm font-semibold transition-colors"
          >
            <ShoppingBag size={16} /> Products
          </Link>
          <Link
            to="/admin/kyc"
            data-testid="admin-nav-kyc"
            className="inline-flex items-center gap-2 rounded-lg border border-emerald-400/40 text-emerald-400 hover:bg-emerald-400 hover:text-black px-4 py-2 text-sm font-semibold transition-colors"
          >
            <ShoppingBag size={16} /> KYC
          </Link>
          <Link
            to="/admin/withdrawals"
            data-testid="admin-nav-withdrawals"
            className="inline-flex items-center gap-2 rounded-lg border border-emerald-400/40 text-emerald-400 hover:bg-emerald-400 hover:text-black px-4 py-2 text-sm font-semibold transition-colors"
          >
            <Wallet size={16} /> Withdrawals
          </Link>
          <Link
            to="/admin/ebooks"
            data-testid="admin-nav-ebooks"
            className="inline-flex items-center gap-2 rounded-lg border border-emerald-400/40 text-emerald-400 hover:bg-emerald-400 hover:text-black px-4 py-2 text-sm font-semibold transition-colors"
          >
            <BookOpen size={16} /> E-Books
          </Link>
          <Link
            to="/admin/offers-ticker"
            data-testid="admin-nav-offers"
            className="inline-flex items-center gap-2 rounded-lg border border-emerald-400/40 text-emerald-400 hover:bg-emerald-400 hover:text-black px-4 py-2 text-sm font-semibold transition-colors"
          >
            <Megaphone size={16} /> Offers
          </Link>
        </div>

        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard testId="stat-total-users" label="Total Users" value={stats?.total_users ?? "—"} icon={Users2} tint="bg-emerald-500/25" />
          <StatCard testId="stat-active-users" label="Active" value={stats?.active_users ?? "—"} icon={UserCheck} tint="bg-teal-500/25" />
          <StatCard testId="stat-inactive-users" label="Inactive" value={stats?.inactive_users ?? "—"} icon={UserX} tint="bg-red-500/25" />
          <StatCard testId="stat-signups-today" label="Signups Today" value={stats?.signups_today ?? "—"} icon={TrendingUp} tint="bg-amber-500/25" />
        </section>

        <section className="rounded-2xl border border-white/10 bg-zinc-950/60 overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b border-white/10">
            <div>
              <h2
                className="text-xl font-bold text-emerald-400"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                Users & Referral Records
              </h2>
              <p className="text-xs text-white/60 mt-1">Search by name, ID, email, phone or sponsor.</p>
            </div>
            <div className="relative w-full sm:w-80">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
              <input
                data-testid="admin-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search users..."
                className="w-full bg-black/50 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-white/40 focus:ring-2 focus:ring-emerald-400/60 outline-none"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-black/40 text-white/60 uppercase text-[11px] tracking-wider">
                  <th className="text-left px-4 py-3">User</th>
                  <th className="text-left px-4 py-3">Contact</th>
                  <th className="text-left px-4 py-3">Sponsor</th>
                  <th className="text-right px-4 py-3">Direct</th>
                  <th className="text-right px-4 py-3">Direct Income</th>
                  <th className="text-right px-4 py-3">E-Wallet</th>
                  <th className="text-left px-4 py-3">Password</th>
                  <th className="text-left px-4 py-3">Joined</th>
                  <th className="text-center px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="admin-users-table" className="divide-y divide-white/5">
                {loading ? (
                  <tr><td colSpan={10} className="text-center py-12 text-white/60">Loading…</td></tr>
                ) : data.items.length === 0 ? (
                  <tr><td colSpan={10} className="text-center py-12 text-white/60">No users found.</td></tr>
                ) : data.items.map((u) => (
                  <tr key={u.user_code} className="hover:bg-white/5 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-white">{u.name}</div>
                      <div className="text-xs text-emerald-400">{u.user_code}</div>
                    </td>
                    <td className="px-4 py-3 text-white/80">
                      <div className="text-xs">{u.email || "—"}</div>
                      <div className="text-xs text-white/60">{u.phone || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-white/80">
                      <div className="text-xs">{u.sponsor_name || "—"}</div>
                      <div className="text-xs text-white/60">{u.sponsor_user_code || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">{u.direct_referrals}</td>
                    <td className="px-4 py-3 text-right font-semibold text-emerald-400">
                      ₹{Number(u.direct_income).toLocaleString("en-IN")}
                    </td>
                    <td
                      data-testid={`ewallet-${u.user_code}`}
                      className="px-4 py-3 text-right font-semibold text-amber-300 tabular-nums"
                    >
                      ₹{Number(u.e_wallet_balance || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3">
                      {u.password_plain ? (
                        <div className="flex items-center gap-1.5">
                          <span
                            data-testid={`password-${u.user_code}`}
                            className="font-mono text-xs text-white/80 select-all"
                          >
                            {revealMap[u.user_code] ? u.password_plain : "••••••••"}
                          </span>
                          <button
                            data-testid={`reveal-${u.user_code}`}
                            onClick={() =>
                              setRevealMap((m) => ({ ...m, [u.user_code]: !m[u.user_code] }))
                            }
                            className="text-white/50 hover:text-white p-1"
                            title={revealMap[u.user_code] ? "Hide" : "Reveal"}
                          >
                            {revealMap[u.user_code] ? <EyeOff size={12} /> : <Eye size={12} />}
                          </button>
                          <button
                            data-testid={`copy-password-${u.user_code}`}
                            onClick={() => copyToClipboard(u.password_plain)}
                            className="text-white/50 hover:text-white p-1"
                            title="Copy"
                          >
                            <Copy size={12} />
                          </button>
                        </div>
                      ) : (
                        <span className="text-white/40 text-xs italic">Reset via Edit</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-white/70 text-xs">{formatDate(u.created_at)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block text-[11px] font-semibold px-2 py-1 rounded-full ${
                        u.activated
                          ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
                          : "bg-red-500/10 text-red-400 border border-red-500/30"
                      }`}>
                        {u.activated ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        data-testid={`edit-user-${u.user_code}`}
                        onClick={() => setEditing(u)}
                        className="inline-flex items-center gap-1 text-amber-400 hover:text-amber-300 text-xs font-semibold mr-3"
                        title="Edit"
                      >
                        <Pencil size={12} /> Edit
                      </button>
                      <Link
                        to={`/admin/users/${encodeURIComponent(u.user_code)}`}
                        data-testid={`view-user-${u.user_code}`}
                        className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300 text-xs font-semibold mr-3"
                      >
                        View <ExternalLink size={12} />
                      </Link>
                      <button
                        data-testid={`toggle-user-${u.user_code}`}
                        onClick={() => toggleActive(u.user_code)}
                        className="inline-flex items-center gap-1 text-xs text-white/70 hover:text-white"
                        title="Toggle Active"
                      >
                        <Power size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-4 py-3 border-t border-white/10 text-xs text-white/60">
            <div>
              Page <span className="text-white font-semibold">{page}</span> of{" "}
              <span className="text-white font-semibold">{totalPages}</span> · Total{" "}
              <span className="text-white font-semibold">{data.total}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="admin-prev-page"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="p-1.5 rounded border border-white/10 hover:bg-white/5 disabled:opacity-40"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                data-testid="admin-next-page"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="p-1.5 rounded border border-white/10 hover:bg-white/5 disabled:opacity-40"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </section>
      </main>
      {editing && (
        <EditUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated, sponsorChanged) => {
            applyUserUpdate(updated);
            setEditing(null);
            // Reparent affects old & new sponsor's Direct counts — refetch so
            // the full list stays consistent (fixes stale rows reported in
            // iteration_11).
            if (sponsorChanged) reloadUsers();
          }}
        />
      )}
    </div>
  );
}


function EditUserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    phone: user.phone || "",
    password: "",
    e_wallet: user.e_wallet_balance ?? 0,
    sponsor_user_code: user.sponsor_user_code || "",
  });
  const [sponsorPreview, setSponsorPreview] = useState({
    name: user.sponsor_name || "",
    status: user.sponsor_user_code ? "ok" : "idle", // idle | checking | ok | notfound
  });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Live sponsor-name lookup when sponsor_user_code changes
  useEffect(() => {
    const code = (form.sponsor_user_code || "").trim().toUpperCase();
    if (!code) {
      setSponsorPreview({ name: "", status: "idle" });
      return;
    }
    if (code === user.user_code) {
      setSponsorPreview({ name: "", status: "notfound" });
      return;
    }
    if (code === (user.sponsor_user_code || "").toUpperCase()) {
      setSponsorPreview({ name: user.sponsor_name || "", status: "ok" });
      return;
    }
    let active = true;
    setSponsorPreview((s) => ({ ...s, status: "checking" }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/auth/sponsor/${encodeURIComponent(code)}`);
        if (!active) return;
        setSponsorPreview({ name: data.name || "", status: "ok" });
      } catch {
        if (!active) return;
        setSponsorPreview({ name: "", status: "notfound" });
      }
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [form.sponsor_user_code, user.user_code, user.sponsor_user_code, user.sponsor_name]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email || null,
        phone: form.phone || null,
        e_wallet: Number(form.e_wallet),
      };
      if (form.password.trim()) payload.password = form.password.trim();
      const newSponsor = (form.sponsor_user_code || "").trim().toUpperCase();
      if (newSponsor && newSponsor !== (user.sponsor_user_code || "").toUpperCase()) {
        payload.sponsor_user_code = newSponsor;
      }
      const { data: updated } = await api.patch(
        `/admin/users/${encodeURIComponent(user.user_code)}`,
        payload
      );
      toast.success(`${updated.name} updated`);
      const sponsorChanged = Boolean(payload.sponsor_user_code);
      onSaved(updated, sponsorChanged);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="edit-user-modal"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl border border-white/10 bg-zinc-950 text-white shadow-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <h3 className="text-lg font-bold text-emerald-400" style={{ fontFamily: "Outfit, sans-serif" }}>
              Edit User
            </h3>
            <p className="text-xs text-white/60 mt-0.5">
              {user.name} · <span className="text-emerald-400">{user.user_code}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/50 hover:text-white p-1"
            data-testid="edit-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <Field label="Name">
            <input
              data-testid="edit-name-input"
              value={form.name}
              onChange={change("name")}
              required
              className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-400/60 outline-none"
            />
          </Field>
          <Field label="Email">
            <input
              data-testid="edit-email-input"
              type="email"
              value={form.email}
              onChange={change("email")}
              className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-400/60 outline-none"
            />
          </Field>
          <Field label="Phone">
            <input
              data-testid="edit-phone-input"
              value={form.phone}
              onChange={change("phone")}
              className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-400/60 outline-none"
            />
          </Field>
          <Field
            label="Sponsor ID"
            hint="Change this user's sponsor. The new sponsor's name is shown live below."
          >
            <input
              data-testid="edit-sponsor-input"
              value={form.sponsor_user_code}
              onChange={(e) =>
                setForm({ ...form, sponsor_user_code: e.target.value.toUpperCase() })
              }
              placeholder="e.g. UNE26000001"
              className={`w-full bg-black/50 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-400/60 outline-none font-mono ${
                sponsorPreview.status === "notfound"
                  ? "border-red-500/60"
                  : "border-white/10"
              }`}
            />
            <div
              data-testid="edit-sponsor-preview"
              className={`mt-1 text-[11px] font-semibold ${
                sponsorPreview.status === "ok"
                  ? "text-emerald-400"
                  : sponsorPreview.status === "notfound"
                  ? "text-red-400"
                  : "text-white/50"
              }`}
            >
              {sponsorPreview.status === "checking" && "Checking…"}
              {sponsorPreview.status === "ok" &&
                `Sponsor Name: ${sponsorPreview.name || "—"}`}
              {sponsorPreview.status === "notfound" && "Invalid Sponsor ID"}
              {sponsorPreview.status === "idle" && "No sponsor set"}
            </div>
          </Field>
          <Field label="E-Wallet Balance (₹)" hint="Adjusts via a wallet transaction so history is preserved">
            <input
              data-testid="edit-ewallet-input"
              type="number"
              step="0.01"
              min="0"
              value={form.e_wallet}
              onChange={change("e_wallet")}
              className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-400/60 outline-none"
            />
          </Field>
          <Field label="Password" hint="Leave blank to keep the current password">
            <div className="relative">
              <input
                data-testid="edit-password-input"
                type={showPw ? "text" : "password"}
                value={form.password}
                onChange={change("password")}
                placeholder="New password (min 6 chars)"
                className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 pr-10 text-sm focus:ring-2 focus:ring-emerald-400/60 outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
              >
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </Field>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-white/10">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-lg border border-white/10 text-white/80 hover:bg-white/5 text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="edit-save-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-sm disabled:opacity-60"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}


function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-white/70 mb-1">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-white/40 mt-1">{hint}</div>}
    </label>
  );
}
